import { useState, useRef, useEffect } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { clsx } from 'clsx'
import { resetPassword, forgotPassword } from '../api/auth'

export default function ResetPasswordPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const email = location.state?.email || ''

  const [code, setCode] = useState(['', '', '', '', '', ''])
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [loading, setLoading] = useState(false)
  const [resending, setResending] = useState(false)
  const [resendCooldown, setResendCooldown] = useState(0)

  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  // Redirect if no email
  useEffect(() => {
    if (!email) {
      navigate('/forgot-password')
    }
  }, [email, navigate])

  // Cooldown timer for resend
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [resendCooldown])

  // Auto-focus first input
  useEffect(() => {
    inputRefs.current[0]?.focus()
  }, [])

  const handleCodeChange = (index: number, value: string) => {
    if (value && !/^\d$/.test(value)) return

    const newCode = [...code]
    newCode[index] = value
    setCode(newCode)
    setError('')

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace') {
      if (!code[index] && index > 0) {
        inputRefs.current[index - 1]?.focus()
      }
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
    if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    if (pastedData.length === 6) {
      setCode(pastedData.split(''))
      inputRefs.current[5]?.focus()
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const fullCode = code.join('')

    if (fullCode.length !== 6) {
      setError('Введите 6-значный код')
      return
    }

    if (newPassword.length < 8) {
      setError('Пароль должен быть минимум 8 символов')
      return
    }

    if (newPassword !== confirmPassword) {
      setError('Пароли не совпадают')
      return
    }

    setLoading(true)
    setError('')

    try {
      await resetPassword(email, fullCode, newPassword)
      setSuccess(true)
      setTimeout(() => navigate('/login', { state: { passwordReset: true } }), 2000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось сбросить пароль')
    } finally {
      setLoading(false)
    }
  }

  const handleResend = async () => {
    if (resendCooldown > 0) return

    setResending(true)
    setError('')

    try {
      await forgotPassword(email)
      setResendCooldown(60)
      setCode(['', '', '', '', '', ''])
      inputRefs.current[0]?.focus()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось отправить код')
    } finally {
      setResending(false)
    }
  }

  if (success) {
    return (
      <div className="min-h-screen flex flex-col justify-center px-4 py-6 sm:px-6 sm:py-12 bg-gradient-calm">
        <div className="sm:mx-auto sm:w-full sm:max-w-md animate-fade-in">
          <div className="card-elevated py-8 px-6 sm:px-10 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-success-light rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-success-dark" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-neutral-800 mb-2">Пароль изменён!</h2>
            <p className="text-neutral-500">Перенаправляем на страницу входа...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-col justify-center px-4 py-6 sm:px-6 sm:py-12 bg-gradient-calm">
      <div className="sm:mx-auto sm:w-full sm:max-w-md animate-fade-in">
        {/* Logo */}
        <div className="flex justify-center mb-4 sm:mb-6">
          <div className="relative">
            <div className="absolute -inset-4 bg-primary-200 rounded-full blur-xl opacity-50" />
            <div className="relative w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-primary-400 to-primary-600 rounded-2xl sm:rounded-3xl flex items-center justify-center shadow-primary">
              <svg className="w-8 h-8 sm:w-10 sm:h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
              </svg>
            </div>
          </div>
        </div>

        <h1 className="text-center text-2xl sm:text-3xl font-bold text-neutral-800">
          Новый пароль
        </h1>
        <p className="mt-2 text-center text-sm sm:text-base text-neutral-500">
          Код отправлен на <span className="font-medium text-neutral-700">{email}</span>
        </p>
      </div>

      <div className="mt-4 sm:mt-8 sm:mx-auto sm:w-full sm:max-w-md animate-slide-up">
        <div className="card-elevated py-6 px-5 sm:py-8 sm:px-10">
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="bg-error-light text-error-dark p-4 rounded-2xl text-sm flex items-center gap-2">
                <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                {error}
              </div>
            )}

            {/* 6-digit code input */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Код из письма
              </label>
              <div className="flex justify-center gap-2 sm:gap-3">
                {code.map((digit, index) => (
                  <input
                    key={index}
                    ref={el => { inputRefs.current[index] = el }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={e => handleCodeChange(index, e.target.value)}
                    onKeyDown={e => handleKeyDown(index, e)}
                    onPaste={index === 0 ? handlePaste : undefined}
                    disabled={loading}
                    className={clsx(
                      'w-10 h-12 sm:w-11 sm:h-14 text-center text-xl font-bold rounded-xl border-2 transition-all',
                      'focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-400',
                      digit ? 'border-primary-300 bg-primary-50' : 'border-neutral-200 bg-white',
                      loading && 'opacity-50 cursor-wait'
                    )}
                  />
                ))}
              </div>
            </div>

            {/* New password */}
            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-neutral-700 mb-1.5">
                Новый пароль
              </label>
              <input
                id="newPassword"
                type="password"
                required
                minLength={8}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="input"
                placeholder="Минимум 8 символов"
              />
            </div>

            {/* Confirm password */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-neutral-700 mb-1.5">
                Подтвердите пароль
              </label>
              <input
                id="confirmPassword"
                type="password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="input"
                placeholder="Повторите пароль"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className={clsx(
                'btn-primary w-full text-base py-3 sm:py-3.5',
                loading && 'opacity-70 cursor-wait'
              )}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Сохраняем...
                </span>
              ) : (
                'Сохранить пароль'
              )}
            </button>
          </form>

          {/* Resend code */}
          <div className="mt-4 text-center">
            <button
              onClick={handleResend}
              disabled={resending || resendCooldown > 0}
              className={clsx(
                'text-sm transition-colors',
                resendCooldown > 0
                  ? 'text-neutral-400 cursor-not-allowed'
                  : 'text-primary-600 hover:text-primary-700'
              )}
            >
              {resending ? (
                'Отправляем...'
              ) : resendCooldown > 0 ? (
                `Отправить повторно (${resendCooldown}с)`
              ) : (
                'Отправить код повторно'
              )}
            </button>
          </div>

          <p className="mt-6 text-center text-sm text-neutral-500">
            <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium transition-colors">
              Вернуться к входу
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}
